import { computed } from 'vue';
import { useStoreGetters, useMapGetter } from 'dashboard/composables/store';
import { useAlert, useTrack } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import { OPEN_AI_EVENTS } from 'dashboard/helper/AnalyticsHelper/events';
import OpenAPI from 'dashboard/api/integrations/openapi';
import { convertHtmlToMarkdown } from '../helper/editorHelper';
import { getLanguageDisplayName } from '../helper/languageHelper';

/**
 * Cleans and normalizes a list of labels.
 * @param {string} labels - A comma-separated string of labels.
 * @returns {string[]} An array of cleaned and unique labels.
 */
const cleanLabels = labels => {
  return labels
    .toLowerCase() // Set it to lowercase
    .split(',') // split the string into an array
    .filter(label => label.trim()) // remove any empty strings
    .map(label => label.trim()) // trim the words
    .filter((label, index, self) => self.indexOf(label) === index);
};

/**
 * A composable function for AI-related operations in the dashboard.
 * @returns {Object} An object containing AI-related methods and computed properties.
 */
export function useAI() {
  const getters = useStoreGetters();
  const { t, locale } = useI18n();

  /**
   * Computed property for UI flags.
   * @type {import('vue').ComputedRef<Object>}
   */
  const uiFlags = computed(() => getters['integrations/getUIFlags'].value);

  const currentChat = useMapGetter('getSelectedChat');
  const inboxById = useMapGetter('inboxes/getInboxById');
  const replyMode = useMapGetter('draftMessages/getReplyEditorMode');

  /**
   * The language the "translate" action translates the draft into: the conversation's
   * (customer's) detected language, falling back to the language the inbox is configured
   * to speak when detection found nothing to go on. Short messages often carry no signal
   * at all, and the fallback is opt-in per inbox. Empty when neither is available — in
   * which case translation is not offered.
   * @type {import('vue').ComputedRef<string>}
   */
  const translationTargetLanguage = computed(
    () =>
      currentChat.value?.additional_attributes?.conversation_language ||
      inboxById.value?.(currentChat.value?.inbox_id)
        ?.fallback_conversation_language ||
      ''
  );

  /**
   * Human-readable name of the translation target language, localized into the agent's
   * UI locale (e.g. 'cs' -> 'Czech'). Empty when no language has been detected.
   * @type {import('vue').ComputedRef<string>}
   */
  const translationTargetLanguageLabel = computed(() =>
    getLanguageDisplayName(translationTargetLanguage.value, locale.value)
  );

  /**
   * Computed property to check if AI integration is enabled.
   * Check if OPENAI_API_KEY is configured on the server.
   * @type {import('vue').ComputedRef<boolean>}
   */
  const isAIIntegrationEnabled = computed(() => true);

  /**
   * Computed property to check if label suggestion feature is enabled.
   * @type {import('vue').ComputedRef<boolean>}
   */
  const isLabelSuggestionFeatureEnabled = computed(() => false);

  /**
   * Computed property for the conversation ID.
   * @type {import('vue').ComputedRef<string|undefined>}
   */
  const conversationId = computed(() => currentChat.value?.id);

  /**
   * Computed property for the draft message.
   * Reads from the backend-backed messageDraft store.
   * @type {import('vue').ComputedRef<string>}
   */
  const draftMessage = computed(() => {
    const draft = getters['messageDraft/getDraft'].value(conversationId.value);
    if (!draft) return '';
    const isPrivateNote = replyMode.value === 'note';
    return (isPrivateNote ? draft.privateContent : draft.publicContent) || '';
  });

  /**
   * Records analytics for AI-related events.
   * @param {string} type - The type of event.
   * @param {Object} payload - Additional data for the event.
   * @returns {Promise<void>}
   */
  const recordAnalytics = async (type, payload) => {
    const event = OPEN_AI_EVENTS[type.toUpperCase()];
    if (event) {
      useTrack(event, {
        type,
        ...payload,
      });
    }
  };

  /**
   * Fetches label suggestions for the current conversation.
   * @returns {Promise<string[]>} An array of suggested labels.
   */
  const fetchLabelSuggestions = async () => {
    if (!conversationId.value) return [];

    try {
      const result = await OpenAPI.processEvent({
        type: 'label_suggestion',
        conversationId: conversationId.value,
      });

      const {
        data: { message: labels },
      } = result;

      return cleanLabels(labels);
    } catch {
      return [];
    }
  };

  /**
   * Processes an AI event, such as rephrasing content.
   * @param {string} [type='rephrase'] - The type of AI event to process.
   * @param {Object} [overrides] - Supplied by callers with no selected conversation.
   * @param {string} [overrides.content] - Text to act on instead of the conversation draft.
   * @param {string|number} [overrides.conversationId] - Conversation display id to send instead
   *   of the selected chat's.
   * @returns {Promise<string>} The generated message or an empty string if an error occurs.
   */
  const processEvent = async (type = 'rephrase', overrides = {}) => {
    // Defaults to the selected conversation's draft. Callers that have no conversation — the
    // compose-new-conversation modal — pass their own editor content and id instead.
    let content = overrides.content ?? draftMessage.value ?? '';
    const targetConversationId =
      overrides.conversationId ?? conversationId.value;

    if (content.startsWith('<')) {
      content = convertHtmlToMarkdown(content);
    }

    try {
      const result = await OpenAPI.processEvent({
        type,
        content: content,
        conversationId: targetConversationId,
      });
      const {
        data: { message: generatedMessage },
      } = result;
      return generatedMessage;
    } catch (error) {
      const errorData = error.response.data.error;
      const errorMessage =
        errorData?.error?.message ||
        t('INTEGRATION_SETTINGS.OPEN_AI.GENERATE_ERROR');
      useAlert(errorMessage);
      return '';
    }
  };

  return {
    draftMessage,
    uiFlags,
    currentChat,
    replyMode,
    isAIIntegrationEnabled,
    isLabelSuggestionFeatureEnabled,
    translationTargetLanguage,
    translationTargetLanguageLabel,
    recordAnalytics,
    fetchLabelSuggestions,
    processEvent,
  };
}
